/**
 * Zeroday Breadth panel data: MAG7, VIX, VVIX, house $ADD and house $ADDQ.
 *
 * QuantForge `GET /v1/steamroom/zeroday-breadth` returns them as 1-minute
 * RTH values.
 *
 * MAG7 (the pane label; the wire key stays `fatmann`) is the Steamroom
 * Watchdog Pine study, computed by QF on 1-minute bars. Only its values come
 * back. Never recompute it here: the formula stays out of the bundle, and its
 * eight stocks are outside the zeroday scope.
 *
 * It is a FIXED-SYMBOL route: the caller names no ticker. `vix` and `vvix`
 * are 1-minute RTH closes of those indexes. Keep both on this route so the
 * vol pane can switch without a second request. A response from before `vvix`
 * shipped parses that series as empty.
 *
 * ADD/ADDQ also stream live on Centrifugo `internals:market`, one snapshot per
 * second. Portal must allowlist that channel (`_FIREHOSE_CHANNELS`) or the
 * subscription 403s at our own Django. The vol indexes have no live tail
 * here; the REST poll refreshes them.
 *
 * ⚠️ ADD/ADDQ are QF's house computation, not the NYSE/Nasdaq published
 * values: lit-venue last sales, and the last RTH print as yesterday's close.
 * Shape tracks TradingView's $ADD; levels run lower.
 */

import { isRth } from '../components/candle-chart/shareMinutes';
import { qfRestGet } from './qfClient';

const ZERODAY_BREADTH_PATH = '/v1/steamroom/zeroday-breadth';
export const INTERNALS_CHANNEL = 'internals:market';

export type BreadthSeriesKey = 'fatmann' | 'vix' | 'vvix' | 'add' | 'addq';

/** One 1-minute close. `time` is the minute's OPEN in epoch seconds. */
export interface BreadthPoint {
  time: number;
  value: number;
}

export type ZerodayBreadthSeries = Record<BreadthSeriesKey, BreadthPoint[]>;

/** One live `internals:market` snapshot, bucketed to its minute. */
export interface InternalsTick {
  minute: number;
  add: number;
  addq: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parsePoints(raw: unknown): BreadthPoint[] {
  if (!Array.isArray(raw)) return [];
  const out: BreadthPoint[] = [];
  for (const row of raw) {
    if (!isRecord(row)) continue;
    const { time, value } = row;
    if (typeof time !== 'number' || typeof value !== 'number') continue;
    if (!Number.isFinite(time) || !Number.isFinite(value)) continue;
    out.push({ time, value });
  }
  return out.sort((a, b) => a.time - b.time);
}

/** A missing or malformed series is empty, never a thrown error: one dark source must not blank the widget. */
export function parseZerodayBreadth(raw: unknown): ZerodayBreadthSeries {
  const series = isRecord(raw) && isRecord(raw.series) ? raw.series : {};
  return {
    fatmann: parsePoints(series.fatmann),
    vix: parsePoints(series.vix),
    vvix: parsePoints(series.vvix),
    add: parsePoints(series.add),
    addq: parsePoints(series.addq),
  };
}

export async function fetchZerodayBreadth(sessions = 2): Promise<ZerodayBreadthSeries> {
  const body = await qfRestGet<unknown>(ZERODAY_BREADTH_PATH, { sessions: String(sessions) });
  return parseZerodayBreadth(body);
}

/** Parses a live snapshot. Outside RTH it returns null, matching the REST series. */
export function parseInternalsTick(raw: unknown): InternalsTick | null {
  if (!isRecord(raw)) return null;
  const { ts, add, addq } = raw;
  if (typeof ts !== 'string' || typeof add !== 'number' || typeof addq !== 'number') return null;
  const ms = Date.parse(ts);
  if (!Number.isFinite(ms) || !isRth(ms)) return null;
  return { minute: Math.floor(ms / 60_000) * 60, add, addq };
}

/**
 * Folds a live value into a series: the current minute is overwritten (the
 * last snapshot in a minute is its close), a new minute is appended, and an
 * older minute is ignored so a late frame cannot rewrite history.
 */
export function mergeLivePoint(points: readonly BreadthPoint[], minute: number, value: number): BreadthPoint[] {
  const last = points[points.length - 1];
  if (last && minute < last.time) return points as BreadthPoint[];
  if (last && minute === last.time) {
    if (last.value === value) return points as BreadthPoint[];
    return [...points.slice(0, -1), { time: minute, value }];
  }
  return [...points, { time: minute, value }];
}
